import { createSignal } from "solid-js";

import { closestSelector } from "@/lib/gestures/guards";
import { applyRubberBand, createGestureRecognizer } from "@/lib/gestures/recognizer";
import { motionDurationMs } from "@/lib/gestures/reduced-motion";
import { pointerHandlers } from "@/lib/gestures/solid";
import { TICKET_LINE_SWIPE } from "@/lib/gestures/presets";

interface TicketLineSwipeOptions {
  /** Whether the gesture is allowed (e.g. order not yet paid). */
  enabled: () => boolean;
  /** Remove the line from the order. */
  onRemove: () => void;
  /** Width of the swiped surface, for the slide-out distance. */
  getSurfaceRef: () => HTMLElement | undefined;
}

const {
  thresholdPx: THRESHOLD_PX,
  commitThresholdPx: COMMIT_THRESHOLD_PX,
  rubberBandLimitPx: RUBBER_BAND_LIMIT,
  rubberBandFactor: RUBBER_BAND_FACTOR,
  hapticMs: HAPTIC_MS,
  commitAnimationMs: COMMIT_ANIMATION_MS,
  cancelAnimationMs: CANCEL_ANIMATION_MS,
  fallbackWidthPx: FALLBACK_WIDTH_PX,
  fling: FLING,
} = TICKET_LINE_SWIPE;

/**
 * Swipe-to-remove for a POS ticket line. Swiping left past the commit threshold
 * (or flinging) slides the line out and removes it; anything short bounces back.
 * Only the left direction commits — a destructive action — and only when not yet
 * paid. Right-drag rubber-bands and snaps back so the gesture stays single-axis.
 */
export function useTicketLineSwipe(options: TicketLineSwipeOptions) {
  const [tx, setTx] = createSignal(0);
  const [isSwiping, setIsSwiping] = createSignal(false);
  const [isAnimating, setIsAnimating] = createSignal(false);

  let hasSwiped = false;

  const recognizer = createGestureRecognizer({
    axis: "x",
    thresholdPx: THRESHOLD_PX,
    commitPx: COMMIT_THRESHOLD_PX,
    haptics: true,
    hapticMs: HAPTIC_MS,
    fling: FLING,
    canStart: ({ target }) => {
      if (!options.enabled()) {
        return false;
      }
      // Ignore drags that begin on interactive controls.
      return !closestSelector(target, 'button, a, input, select, [data-no-swipe="true"]');
    },
    onStart: () => {
      setIsSwiping(true);
      setIsAnimating(false);
    },
    onMove: (sample) => {
      // Only allow leftward travel; rubber-band the rest.
      const dx = Math.min(sample.dx, 0);
      setTx(applyRubberBand(dx, RUBBER_BAND_LIMIT, RUBBER_BAND_FACTOR));
    },
    onEnd: (sample) => {
      hasSwiped = true;
      setIsSwiping(false);
      setIsAnimating(true);

      const finalTx = tx();
      const commitLeft =
        finalTx < -COMMIT_THRESHOLD_PX || (sample.isFling && sample.direction === "left");

      if (commitLeft) {
        const width = options.getSurfaceRef()?.clientWidth ?? FALLBACK_WIDTH_PX;
        setTx(-width);
        setTimeout(() => {
          options.onRemove();
          setTx(0);
          setIsAnimating(false);
        }, motionDurationMs(COMMIT_ANIMATION_MS));
      } else {
        setTx(0);
        setTimeout(() => {
          setIsAnimating(false);
        }, motionDurationMs(CANCEL_ANIMATION_MS));
      }
    },
    onCancel: () => {
      setIsSwiping(false);
      setIsAnimating(true);
      setTx(0);
      setTimeout(() => {
        setIsAnimating(false);
      }, motionDurationMs(CANCEL_ANIMATION_MS));
    },
  });

  const base = pointerHandlers(recognizer);

  return {
    tx,
    isSwiping,
    isAnimating,
    handlePointerDown: base.onPointerDown,
    handlePointerMove: base.onPointerMove,
    handlePointerUp: base.onPointerUp,
    handlePointerCancel: base.onPointerCancel,
    getHasSwiped: () => hasSwiped,
    resetHasSwiped: () => {
      hasSwiped = false;
    },
    COMMIT_THRESHOLD_PX,
  };
}
